import type {
  BacklightReport,
  PanelDesign,
  PanelMesh,
  PanelRef,
  Vec2,
  WallMetrics,
} from '../types';
import { offsetConvexPolygon } from '../shapes/tessellate';
import { buildFrameMesh, type FrameSpec } from './frame';
import {
  createRimHeightSampler,
  createStripRouting,
  measureStrip,
  traceStrip,
  type StripPath,
} from './routing';
import { MIN_DIFFUSER_MM, type ChannelField } from './channel';
import {
  checkBlending,
  checkTransmission,
  checkUniformity,
  ledType,
  panelMaterial,
  transmission,
} from './light';

/**
 * Assembling a backlit wall.
 *
 * Three things come together here: the frames that hold the strip at a
 * distance, the routing that decides where the strip runs and where it leaves
 * each box, and the optics that say whether the result will actually look like
 * a lit surface.
 *
 * The last of those is the part the first version of this feature was missing.
 * Geometry alone will happily produce a design that prints perfectly and glows
 * like a string of bulbs, so the checks are computed here and reported
 * alongside the parts rather than left for someone to discover after a print.
 */

export interface BuildBacklightOptions {
  design: PanelDesign;
  panels: readonly PanelRef[];
  outline: readonly Vec2[];
  footprint: { minX: number; minY: number; maxX: number; maxY: number };
  wall: WallMetrics;
  /** The generated panels, used to measure how much light gets through them. */
  panelMeshes: readonly PanelMesh[];
  /** The channel cut into those panels, when that is the mounting. */
  channel?: ChannelField | null;
}

export interface BacklightResult {
  frames: PanelMesh[];
  strip: StripPath[];
  report: BacklightReport | null;
}

const EMPTY: BacklightResult = { frames: [], strip: [], report: null };

/** Subdivisions per frame outline edge — enough to resolve a slot edge. */
function frameSegments(panelSize: number, stripWidth: number): number {
  // A slot has to land on at least a few points to come out the right width.
  return Math.max(8, Math.min(64, Math.round((panelSize / Math.max(stripWidth, 4)) * 2)));
}

export function buildBacklight(options: BuildBacklightOptions): BacklightResult {
  const { design, panels, outline, footprint, wall, panelMeshes } = options;
  const spec = design.led;

  if (!spec.enabled || panels.length === 0) return EMPTY;
  if (spec.mounting === 'channel') return buildChannelled(options);

  const frameSpec: FrameSpec = {
    airGap: spec.airGap,
    wallThickness: spec.frameWall,
    floorThickness: spec.frameFloor,
  };

  const interior = offsetConvexPolygon(outline, spec.frameWall);
  // A wall thicker than the footprint can take leaves nothing to light.
  if (!interior) return EMPTY;

  const routing = createStripRouting({ spec, wall, interior });
  const fullHeight = spec.frameFloor + spec.airGap;
  const segments = frameSegments(design.panel.size, spec.stripWidth);

  const frames: PanelMesh[] = [];
  for (const panel of panels) {
    const built = buildFrameMesh({
      spec: frameSpec,
      outline,
      rimHeight: createRimHeightSampler(routing, spec, panel, spec.frameFloor, fullHeight),
      segmentsPerEdge: segments,
    });
    if (!built) return EMPTY;

    frames.push({ panel, mesh: built.mesh, bounds: built.bounds });
  }

  const strip = measureStrip(routing, panels, interior, footprint);
  const type = ledType(spec.ledType);
  const material = panelMaterial(spec.panelMaterial);

  // Transmission is decided by how much panel the light has to cross, which is
  // the backing plate plus however much relief sits above that point.
  const light = checkTransmission(design.panel.baseThickness, design.panel.reliefDepth, material);
  const thickest = design.panel.baseThickness + design.panel.reliefDepth;

  const report: BacklightReport = {
    mounting: 'lightbox',
    channel: null,
    totalStripMm: strip.totalMm,
    stripPerPanelMm: strip.perPanelMm,
    wattsTotal: (strip.totalMm / 1000) * type.wattsPerMetre,
    airGapMm: spec.airGap,
    stripWidthMm: spec.stripWidth,
    assemblyDepthMm: spec.frameFloor + spec.airGap + thickest,
    blending: checkBlending(spec.airGap, type),
    uniformity: checkUniformity(routing.runSpacingMm, spec.airGap),
    transmission: light,
  };

  attachGlow(panelMeshes, design, material);

  return { frames, strip: traceStrip(spec, wall, panels, interior), report };
}

/**
 * A wall whose strip lies in the panels themselves.
 *
 * Nothing is built: the channel is already part of every panel by the time
 * this runs, cut into its back by the mesh builder. What is left is to say
 * what it came out as, and the honest part of that is the blending check.
 *
 * A groove puts the emitters a millimetre or two from the surface. No gap
 * that small blends them, so this check will fail for anything but a COB
 * strip, and it is meant to: it is the cost of one part per tile, stated
 * rather than discovered after a print. The light box exists for people who
 * would rather pay the second print than see the dots.
 */
function buildChannelled(options: BuildBacklightOptions): BacklightResult {
  const { design, panels, outline, footprint, wall, panelMeshes, channel } = options;
  const spec = design.led;

  if (!channel?.active) return EMPTY;

  const strip = measureStrip(channel.routing, panels, outline, footprint);
  const type = ledType(spec.ledType);
  const material = panelMaterial(spec.panelMaterial);

  // Light crosses whatever is left over the channel, plus the relief above it.
  const light = checkTransmission(channel.diffuserMm, design.panel.reliefDepth, material);

  const report: BacklightReport = {
    mounting: 'channel',
    channel: {
      seatMm: channel.seatMm,
      emitterMm: channel.emitterMm,
      emitterWidthMm: channel.emitterWidthMm,
      depthMm: channel.depth,
      requestedMm: spec.channelDepth,
      diffuserMm: channel.diffuserMm,
      plateForRequestMm: spec.channelDepth + type.emitterMm + MIN_DIFFUSER_MM,
    },
    totalStripMm: strip.totalMm,
    stripPerPanelMm: strip.perPanelMm,
    wattsTotal: (strip.totalMm / 1000) * type.wattsPerMetre,
    airGapMm: 0,
    stripWidthMm: spec.stripWidth,
    // The panel hangs flat against the wall, so it stands off by its own depth.
    assemblyDepthMm: design.panel.baseThickness + design.panel.reliefDepth,
    // The distance the light has to mix over is the material over the strip.
    blending: checkBlending(channel.diffuserMm, type),
    // A groove has no uniformity rule to satisfy. Run it through the light
    // box's and it demands runs every couple of millimetres, which is not
    // advice anyone can take — and it is advice against the design rather than
    // about it. A millimetre under the surface the light does not spread, so a
    // channelled wall lights over its runs and stays dark between them. That
    // is the look, not a fault, so the spacing is reported and not judged.
    uniformity: {
      ok: true,
      actualMm: channel.routing.runSpacingMm,
      requiredMm: channel.routing.runSpacingMm,
    },
    transmission: light,
  };

  attachChannelGlow(panelMeshes, channel, material);

  return { frames: [], strip: traceStrip(spec, wall, panels, outline), report };
}

/**
 * Per-vertex transmission for a channelled panel.
 *
 * Light only leaves where there is a channel behind the surface, so the glow
 * is the run's own coverage attenuated by what is left above it. The mesh
 * carries both surfaces in this mounting — the back mirrors the top — so the
 * material at a point is the difference between them, read off the mesh rather
 * than recomputed.
 */
function attachChannelGlow(
  panelMeshes: readonly PanelMesh[],
  channel: ChannelField,
  material: ReturnType<typeof panelMaterial>,
): void {
  for (const part of panelMeshes) {
    const { positions } = part.mesh;
    const count = positions.length / 3;
    // Top surface and back are the same size, so the split is the midpoint.
    const half = count / 2;
    const glow = new Float32Array(count);

    for (let i = 0; i < half; i += 1) {
      const top = positions[i * 3 + 2];
      const back = positions[(half + i) * 3 + 2];
      const coverage = channel.depth > 0 ? back / channel.depth : 0;
      const lit = coverage * transmission(top - back, material);
      glow[i] = lit;
      glow[half + i] = lit;
    }

    part.glow = glow;
  }
}

/**
 * Per-vertex transmission for the preview.
 *
 * With a proper air gap the box lights the panel close to evenly, so what
 * decides where light comes out is the panel's own thickness at that point.
 * That is a much simpler model than the previous one — and a more honest one,
 * because it is the thickness, not proximity to a strip, that actually governs.
 */
function attachGlow(
  panelMeshes: readonly PanelMesh[],
  design: PanelDesign,
  material: ReturnType<typeof panelMaterial>,
): void {
  for (const part of panelMeshes) {
    const { positions } = part.mesh;
    const count = positions.length / 3;
    const glow = new Float32Array(count);

    for (let i = 0; i < count; i += 1) {
      // The panel's back face is on z = 0, so a vertex's height above it is the
      // thickness the light has to cross to reach that point.
      glow[i] = transmission(positions[i * 3 + 2], material);
    }

    part.glow = glow;
  }
  void design;
}
